import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  ArrowRight,
  CheckCircle2,
  Lock,
  Search,
  AlertTriangle,
  PhoneCall,
  Eye,
  FileText,
  Clock,
  ChevronDown,
  Users,
  TrendingUp,
  Star,
} from "lucide-react";
import PublicNavbar from "../components/PublicNavbar";
import PublicFooter from "../components/PublicFooter";
import AgencyMark from "../components/AgencyMark";

/* ── Parallax Hook ─────────────────────────────────────────── */
function useParallax(speed = 0.4) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY * speed);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);
  return offset;
}

/* ── Intersection Observer Hook ────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Animated Counter ──────────────────────────────────────── */
function AnimatedCounter({ target, duration = 1800 }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.3);

  useEffect(() => {
    if (!inView) return;
    const numTarget = parseFloat(String(target).replace(/[^0-9.]/g, "")) || 0;
    const step = numTarget / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, numTarget);
      setCount(Math.round(current));
      if (current >= numTarget) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  if (!inView) return <span ref={ref}>0</span>;

  const display =
    String(target).startsWith("<") ? target :
    target === "24/7" ? "24/7" :
    target === "100%" ? `${count}%` :
    `${count}`;

  return <span ref={ref}>{display}</span>;
}

const HERO_BG =
  "https://images.pexels.com/photos/7714697/pexels-photo-7714697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandingPage() {
  const parallax = useParallax(0.3);
  const [heroRef, heroInView] = useInView(0.1);
  const [stepsRef, stepsInView] = useInView(0.1);
  const [featureRef, featureInView] = useInView(0.1);
  const [ctaRef, ctaInView] = useInView(0.1);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-white overflow-x-hidden">
      <PublicNavbar />

      {/* ═══════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative overflow-hidden min-h-screen flex flex-col" style={{ marginTop: "-64px" }}>
        {/* Parallax background */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: `url('${HERO_BG}')`,
            transform: `scale(1.1) translateY(${parallax}px)`,
          }}
          aria-hidden
        />

        {/* Dark gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(6,24,48,0.97) 0%, rgba(11,43,94,0.92) 45%, rgba(6,24,48,0.88) 100%)",
          }}
          aria-hidden
        />

        {/* Dot grid texture */}
        <div className="absolute inset-0 bg-dots opacity-30" aria-hidden />

        {/* Animated scanline */}
        <div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent pointer-events-none"
          style={{ animation: "scanMove 8s linear infinite", top: 0 }}
          aria-hidden
        />

        {/* Content */}
        <div className="relative flex-1 flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-36 pt-32 lg:pt-40">
          <div className="grid lg:grid-cols-12 gap-10 items-center">

            {/* Left column */}
            <div className="lg:col-span-7 text-white">
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs tracking-[0.2em] uppercase font-bold glass-card ${heroInView ? "animate-fade-slide-up" : "opacity-0"}`}
                style={{ borderColor: "rgba(250,204,21,0.4)" }}
              >
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-live-pulse" />
                <span className="text-emerald-300">Aktif Menerima Laporan</span>
                <span className="text-white/40 mx-1">·</span>
                <span className="text-yellow-300">Portal Resmi Ditresnarkoba</span>
              </div>

              {/* Headline */}
              <h1
                className={`mt-7 font-display leading-[0.95] tracking-tighter ${heroInView ? "animate-fade-slide-up delay-200" : "opacity-0"}`}
                style={{ fontSize: "clamp(2.8rem, 6vw, 5rem)" }}
              >
                <span className="text-white">JOGO JATENG:</span>
                <br />
                <span className="gradient-text">SIKAT NARKOBA!</span>
              </h1>

              {/* Subheading */}
              <p
                className={`mt-6 text-lg text-slate-300 max-w-xl leading-relaxed ${heroInView ? "animate-fade-slide-up delay-300" : "opacity-0"}`}
              >
                Portal pelaporan tindak pidana narkotika di wilayah hukum Polda Jawa Tengah.
                Identitas pelapor{" "}
                <span className="text-yellow-300 font-semibold">dilindungi penuh</span>{" "}
                berdasarkan UU Perlindungan Saksi &amp; Korban.
              </p>

              {/* CTA Buttons */}
              <div
                className={`mt-10 flex flex-wrap gap-3 ${heroInView ? "animate-fade-slide-up delay-400" : "opacity-0"}`}
              >
                <Link
                  to="/lapor"
                  data-testid="hero-lapor-btn"
                  className="btn-shimmer inline-flex items-center gap-2.5 rounded-xl px-7 py-4 text-sm font-bold shadow-lg transition-all hover:scale-105"
                  style={{ background: "var(--gradient-gold)", color: "#0b2b5e" }}
                >
                  <AlertTriangle className="h-4 w-4" />
                  Buat Laporan Baru
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/lacak"
                  data-testid="hero-lacak-btn"
                  className="inline-flex items-center gap-2.5 rounded-xl px-7 py-4 text-sm font-semibold glass-card text-white transition-all hover:bg-white/20 hover:scale-105 border border-white/20"
                >
                  <Search className="h-4 w-4 text-cyan-400" />
                  Lacak Status Laporan
                </Link>
              </div>

              {/* Trust badges */}
              <div
                className={`mt-10 flex flex-wrap items-center gap-6 text-xs text-slate-400 ${heroInView ? "animate-fade-slide-up delay-500" : "opacity-0"}`}
              >
                {[
                  { icon: Lock, label: "Identitas Rahasia", color: "text-yellow-400" },
                  { icon: Shield, label: "Penanganan Prioritas", color: "text-blue-400" },
                  { icon: CheckCircle2, label: "Update Real-time", color: "text-emerald-400" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${color}`} />
                    <span className="text-slate-300">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — Stats card */}
            <div
              className={`lg:col-span-5 ${heroInView ? "animate-fade-slide-up delay-300" : "opacity-0"}`}
            >
              <div className="relative rounded-2xl glass-card-dark p-6 shadow-glow overflow-hidden neon-border-blue">
                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] tracking-[0.28em] font-bold uppercase text-cyan-400">
                      Statistik Wilayah
                    </div>
                    <div className="mt-1 font-display text-xl font-extrabold text-white">
                      Polda Jawa Tengah
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AgencyMark type="ditnarkoba" className="h-14 w-14 shrink-0" />
                    <AgencyMark type="polda" className="h-12 w-12 shrink-0" />
                    <AgencyMark type="polri" className="h-12 w-12 shrink-0" />
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    { k: "35", l: "Kab./Kota Terjangkau", color: "text-yellow-400" },
                    { k: "24/7", l: "Layanan Pelaporan", color: "text-cyan-400" },
                    { k: "100%", l: "Kerahasiaan Pelapor", color: "text-emerald-400" },
                    { k: "< 24j", l: "Respon Awal", color: "text-blue-400" },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4 transition-all hover:scale-105"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <div className={`font-display font-black text-2xl ${s.color}`}>
                        <AnimatedCounter target={s.k} />
                      </div>
                      <div className="text-[11px] mt-1 text-slate-400 leading-tight">{s.l}</div>
                    </div>
                  ))}
                </div>

                <a
                  href="tel:110"
                  className="btn-shimmer mt-4 rounded-xl p-4 flex items-center gap-3 transition-all hover:scale-[1.02]"
                  style={{ background: "linear-gradient(135deg, #facc15 0%, #f59e0b 100%)" }}
                >
                  <div className="h-10 w-10 rounded-lg bg-[#0b2b5e] flex items-center justify-center font-bold text-white flex-shrink-0 text-sm">
                    <PhoneCall className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-[#0b2b5e]/70 uppercase tracking-wider">
                      Darurat
                    </div>
                    <div className="font-bold text-[#0b2b5e]">Call Center Polri — 110</div>
                  </div>
                </a>
              </div>
            </div>

          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float text-white/40">
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════════════════════════ */}
      <section ref={stepsRef} className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-dark opacity-50" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center ${stepsInView ? "animate-fade-slide-up" : "opacity-0"}`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0b2b5e]/20 bg-blue-50 text-[#0b2b5e] text-xs font-bold tracking-[0.2em] uppercase mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0b2b5e]" />
              Alur Pelaporan
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-[#0f172a] max-w-2xl mx-auto">
              Tiga langkah <span className="gradient-text-navy">sederhana</span> untuk melaporkan.
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              {
                num: "01",
                icon: FileText,
                iconColor: "text-blue-600",
                iconBg: "bg-blue-50",
                borderColor: "border-blue-200",
                t: "Isi Formulir Laporan",
                d: "Sampaikan kronologi kejadian secara rinci. Anda dapat memilih melapor secara anonim atau menyertakan identitas.",
                accentLine: "from-blue-400 to-cyan-400",
              },
              {
                num: "02",
                icon: Eye,
                iconColor: "text-yellow-600",
                iconBg: "bg-yellow-50",
                borderColor: "border-yellow-200",
                t: "Unggah Bukti Pendukung",
                d: "Lampirkan foto, dokumen, atau scan KTP untuk memperkuat laporan. Semua data disimpan terenkripsi dan aman.",
                accentLine: "from-yellow-400 to-orange-400",
              },
              {
                num: "03",
                icon: Clock,
                iconColor: "text-emerald-600",
                iconBg: "bg-emerald-50",
                borderColor: "border-emerald-200",
                t: "Pantau Status Laporan",
                d: "Gunakan nomor laporan untuk memantau tindak lanjut mulai dari verifikasi hingga hasil penyelidikan.",
                accentLine: "from-emerald-400 to-teal-400",
              },
            ].map((s, idx) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.num}
                  className={`tilt-card group relative rounded-2xl bg-white border-2 ${s.borderColor} p-8 overflow-hidden ${stepsInView ? "animate-fade-slide-up" : "opacity-0"}`}
                  style={{ animationDelay: `${idx * 0.15}s` }}
                >
                  <div className="absolute -top-3 -right-2 font-display font-black text-[8rem] leading-none text-slate-100 select-none pointer-events-none group-hover:text-slate-200 transition-colors">
                    {s.num}
                  </div>
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${s.accentLine}`} />
                  <div className={`relative h-12 w-12 rounded-xl ${s.iconBg} flex items-center justify-center mb-6 transition-transform group-hover:scale-110`}>
                    <Icon className={`h-6 w-6 ${s.iconColor}`} />
                  </div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-slate-400 mb-3">
                    Langkah {s.num}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#0f172a] tracking-tight relative">
                    {s.t}
                  </h3>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed relative">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          FEATURE BAND
      ═══════════════════════════════════════════════════════ */}
      <section
        ref={featureRef}
        className="py-20 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 50%, #061830 100%)" }}
      >
        <div className="absolute inset-0 bg-dots opacity-20" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-14 ${featureInView ? "animate-fade-slide-up" : "opacity-0"}`}>
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-yellow-400 mb-3">Keunggulan Layanan</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tighter">
              Mengapa Laporan Anda Aman Bersama Kami?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                iconColor: "text-blue-400",
                gradientBorder: "rgba(59,130,246,0.4)",
                t: "Dioperasikan Petugas Resmi",
                d: "Seluruh laporan ditangani langsung oleh Ditresnarkoba Polda Jateng yang berwenang.",
                tag: "Resmi",
              },
              {
                icon: Lock,
                iconColor: "text-yellow-400",
                gradientBorder: "rgba(250,204,21,0.4)",
                t: "Identitas Terlindungi",
                d: "Pelapor dapat memilih identitas tersembunyi. Akses data dibatasi ketat sesuai regulasi.",
                tag: "Aman",
              },
              {
                icon: Eye,
                iconColor: "text-emerald-400",
                gradientBorder: "rgba(52,211,153,0.4)",
                t: "Transparan & Terukur",
                d: "Setiap laporan memiliki nomor unik dan dapat dilacak perkembangannya secara real-time.",
                tag: "Terbuka",
              },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className={`glass-card rounded-2xl p-8 group hover:scale-[1.02] transition-all ${featureInView ? "animate-fade-slide-up" : "opacity-0"}`}
                  style={{
                    animationDelay: `${i * 0.15}s`,
                    boxShadow: `0 0 0 1px ${b.gradientBorder}, 0 4px 24px rgba(0,0,0,0.2)`,
                  }}
                >
                  <div className="mb-5 inline-flex h-14 w-14 rounded-2xl items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${b.gradientBorder}` }}>
                    <Icon className={`h-7 w-7 ${b.iconColor}`} />
                  </div>
                  <div className={`inline-block ml-2 text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full text-xs mb-4 ${b.iconColor}`}
                    style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${b.gradientBorder}` }}>
                    {b.tag}
                  </div>
                  <h3 className="font-display font-bold text-xl text-white">{b.t}</h3>
                  <p className="text-sm text-slate-400 mt-3 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CTA SECTION
      ═══════════════════════════════════════════════════════ */}
      <section
        ref={ctaRef}
        className="relative overflow-hidden py-24 lg:py-32"
        style={{ background: "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 40%, #061830 100%)" }}
      >
        <div className="absolute inset-0 bg-grid-dark opacity-20" aria-hidden />
        <div className="absolute inset-0 bg-dots opacity-10" aria-hidden />

        <div className={`relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${ctaInView ? "animate-fade-slide-up" : "opacity-0"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.3)" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 animate-live-pulse" />
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-yellow-400">Ayo Bertindak</span>
          </div>

          <h3 className="font-display font-black text-white tracking-tighter"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)", lineHeight: "1.05" }}>
            Jawa Tengah bersih narkoba<br />
            <span className="gradient-text">dimulai dari Anda.</span>
          </h3>

          <p className="mt-6 text-slate-300 max-w-2xl mx-auto leading-relaxed text-lg">
            Informasi sekecil apa pun dapat menjadi kunci penyelamatan generasi kita.
            Laporkan sekarang — proses cepat, rahasia, dan resmi.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {[
              { icon: Lock, label: "Identitas Dilindungi" },
              { icon: Shield, label: "Ditangani Resmi" },
              { icon: CheckCircle2, label: "Transparan & Terukur" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-xs text-slate-300 px-4 py-2 rounded-full"
                style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <Icon className="h-3.5 w-3.5 text-yellow-400" />
                {label}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/lapor"
              className="btn-shimmer inline-flex items-center gap-2.5 rounded-xl px-8 py-4 text-sm font-bold shadow-lg hover:scale-105"
              style={{ background: "var(--gradient-gold)", color: "#0b2b5e" }}
            >
              <AlertTriangle className="h-4 w-4" />
              Mulai Laporkan
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/lacak"
              className="inline-flex items-center gap-2.5 rounded-xl border border-white/25 text-white px-8 py-4 text-sm font-semibold hover:bg-white/10 hover:scale-105"
            >
              <Search className="h-4 w-4 text-cyan-400" />
              Cek Laporan Saya
            </Link>
          </div>

          <a href="tel:110" className="mt-10 inline-flex items-center gap-3 group">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
              style={{ background: "rgba(250,204,21,0.15)", border: "1px solid rgba(250,204,21,0.3)" }}>
              <PhoneCall className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="text-left">
              <div className="text-[10px] tracking-wider uppercase text-slate-400">Darurat — Hubungi Langsung</div>
              <div className="text-white font-bold text-sm group-hover:text-yellow-300 transition-colors">Call Center Polri 110</div>
            </div>
          </a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
